import { Text } from '@nautilusbio/nb-design-system/Text';
import { Tooltip } from '@nautilusbio/nb-design-system/Tooltip';
import { clsx } from 'clsx';
import { CircleHelp } from 'lucide-react';
import React, { type FC } from 'react';
import { useTranslation } from 'react-i18next';

import { SIDEBAR_ICON_CONFIG, sidebarTextSize } from '../constants';
import { sidebarLinkClassName } from '../linkStyles';
import styles from '../styles.module.css';

// Not pinned down by QA/POR-273 — no existing help/support URL convention exists elsewhere
// in the app, so this defaults to a support mailbox. See PR description for the assumption.
const HELP_URL = 'mailto:support@nautilus.bio';

export type HelpLinkProps = {
  isSidebarCollapsed: boolean;
};

const HelpLink: FC<HelpLinkProps> = ({ isSidebarCollapsed }) => {
  const { t } = useTranslation('sidebar');

  // Matches NavigationLink's ICON_CONFIG so the icon doesn't look inconsistent in the collapsed state.
  const { size: iconSize, strokeWidth: iconStrokeWidth } = isSidebarCollapsed
    ? SIDEBAR_ICON_CONFIG.collapsed
    : SIDEBAR_ICON_CONFIG.expanded;

  return (
    <Tooltip
      className={styles.zIndex}
      content={t('help')}
      isDisabled={!isSidebarCollapsed}
      offset={2}
      placement="right"
      size="lg"
      transform={false}
      trigger={
        <a
          aria-label={isSidebarCollapsed ? t('help') : undefined}
          className={sidebarLinkClassName}
          data-testid="sidebar-help-link"
          href={HELP_URL}
          rel="noopener noreferrer"
          target="_blank"
        >
          <CircleHelp
            className={isSidebarCollapsed ? styles.iconIsCollapsed : undefined}
            size={iconSize}
            strokeWidth={iconStrokeWidth}
          />

          <Text as="span" className={styles.footerButtonText} size={sidebarTextSize} noWrap>
            {t('help')}
          </Text>
        </a>
      }
      triggerClassName={clsx(styles.tooltip, !isSidebarCollapsed && styles.tooltipExpanded)}
    />
  );
};

export default HelpLink;
